import React, { useState, useEffect, useMemo } from "react";
import { Container, Title, Alert, Paper, Group, Tooltip, Button, ActionIcon, Flex, Pagination, Select, SimpleGrid, Card, Text, Badge, Stack } from "@mantine/core";
import { useStudentData } from "./hooks/useStudentData";
import { StudentFilters } from "./components/StudentFilters";
import { StudentTable } from "./components/StudentTable";
import { StudentModal } from "./components/StudentModal";
import { Student } from "./types/type.student";
import { IconInfoCircle, IconEye } from "@tabler/icons-react";
import saveAs from "file-saver";
import { FaFileCsv, FaFilter, FaRedo, FaSync } from "react-icons/fa";

const StudentsPage: React.FC = () => {
    const {
        students,
        loading,
        error,
        fetchStudents,
        createStudent,
        updateStudent,
        deleteStudent,
        checkEmailExists,
    } = useStudentData();

    // Modal states
    const [modalOpened, setModalOpened] = useState(false);
    const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState<"table" | "card">("table");

    // Filters
    const [filters, setFilters] = useState({
        studentId: "",
        name: "",
        email: "",
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    const handleFilterChange = (field: string, value: string) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const exportStudentsToCSV = (students: Student[]) => {
        if (students.length === 0) {
            alert("No data available for export.");
            return;
        }

        const headers = ["Student ID", "Name", "Email", "Mobile", "Enrollment Date"];
        const csvRows = students.map(student => [
            student.student_id,
            student.name,
            student.email,
            student.mobile,
            student.enrollDate,
        ]);

        const csvContent = [headers, ...csvRows]
            .map(row => row.map(cell => `"${cell || ""}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, `students_export_${new Date().toISOString().split("T")[0]}.csv`);
    };

    const resetFilters = () => {
        setFilters({
            studentId: "",
            name: "",
            email: "",
        });
        setCurrentPage(1);
    };

    const handleCreateStudent = () => {
        setSelectedStudent(null);
        setModalMode("create");
        setModalOpened(true);
    };

    // Opens in read-only view; the modal has an Edit button at the top
    const handleViewStudent = (student: Student) => {
        setSelectedStudent(student);
        setModalMode("view");
        setModalOpened(true);
    };

    const handleCloseModal = () => {
        setModalOpened(false);
    };

    // "create" -> POST (ID generated by the server); anything else -> PUT
    const handleStudentSubmit = async (student: Student) => {
        if (modalMode === "create") {
            return await createStudent(student);
        } else {
            return await updateStudent(student);
        }
    };

    const filteredStudents = useMemo(() => {
        return students.filter(student =>
            (!filters.studentId || student.student_id.includes(filters.studentId)) &&
            (!filters.name || student.name.toLowerCase().includes(filters.name.toLowerCase())) &&
            (!filters.email || student.email.toLowerCase().includes(filters.email.toLowerCase()))
        );
    }, [students, filters]);

    const paginatedStudents = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return filteredStudents.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredStudents, currentPage, rowsPerPage]);

    const handleExportCsv = () => {
        exportStudentsToCSV(filteredStudents);
    };

    useEffect(() => {
        if (currentPage > 1 && paginatedStudents.length === 0 && filteredStudents.length > 0) {
            setCurrentPage(1);
        }
    }, [paginatedStudents, filteredStudents, currentPage]);

    const rangeStart = filteredStudents.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;

    // Card View Component
    const StudentCardView = () => (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {paginatedStudents.map((student) => (
                <Card key={student.student_id} shadow="sm" padding="lg" radius="md" withBorder>
                    <Group justify="space-between" mb="xs">
                        <Badge color="blue" variant="light" size="lg">
                            #{student.student_id}
                        </Badge>
                        <Tooltip label="View / Edit Details">
                            <ActionIcon
                                variant="light"
                                color="blue"
                                onClick={() => handleViewStudent(student)}
                            >
                                <IconEye size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>

                    <Text fw={600} size="lg" mb="xs">
                        {student.name}
                    </Text>

                    <Stack gap="xs">
                        <Group gap="xs">
                            <Text size="sm" c="dimmed">Email:</Text>
                            <Text size="sm">{student.email}</Text>
                        </Group>
                        <Group gap="xs">
                            <Text size="sm" c="dimmed">Mobile:</Text>
                            <Text size="sm">{student.mobile}</Text>
                        </Group>
                        <Group gap="xs">
                            <Text size="sm" c="dimmed">Enrolled:</Text>
                            <Text size="sm">{new Date(student.enrollDate).toLocaleDateString()}</Text>
                        </Group>
                    </Stack>
                </Card>
            ))}
        </SimpleGrid>
    );

    return (
        <Container size="xl" py="xl">
            <Paper
                shadow="sm"
                p="md"
                radius="md"
                withBorder
                style={{
                    backgroundColor: 'var(--mantine-color-gray-0)',
                }}
            >
                {/* Title & Actions */}
                <Group justify="space-between" mb="md">
                    <div>
                        <Title order={2}>All Students</Title>
                        <Text size="sm" c="dimmed">Total: {filteredStudents.length} students</Text>
                    </div>
                    <Group gap="sm">
                        <Tooltip label="Create New Student">
                            <Button variant="filled" color="blue" onClick={handleCreateStudent}>
                                Create Student
                            </Button>
                        </Tooltip>

                        <Tooltip label="Toggle View">
                            <ActionIcon
                                variant="light"
                                color="blue"
                                onClick={() => setViewMode(viewMode === "table" ? "card" : "table")}
                                size="lg"
                            >
                                {viewMode === "table" ? "📱" : "📋"}
                            </ActionIcon>
                        </Tooltip>

                        <Tooltip label="Show Filters">
                            <ActionIcon
                                variant="light"
                                color="blue"
                                onClick={() => setShowFilters(!showFilters)}
                                size="lg"
                            >
                                <FaFilter size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Refresh Data">
                            <ActionIcon variant="light" color="green" onClick={fetchStudents} size="lg">
                                <FaSync size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Reset Filters">
                            <ActionIcon variant="light" color="gray" onClick={resetFilters} size="lg">
                                <FaRedo size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Export to CSV">
                            <ActionIcon variant="light" color="violet" onClick={handleExportCsv} size="lg">
                                <FaFileCsv size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Group>

                {/* Filters */}
                <StudentFilters
                    showFilters={showFilters}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onResetFilters={resetFilters}
                />

                {/* Error Alert */}
                {error && (
                    <Alert icon={<IconInfoCircle size={16} />} title="Error" color="red" mb="md">
                        {error}
                    </Alert>
                )}

                {/* Table or Card View */}
                {viewMode === "table" ? (
                    <StudentTable
                        students={paginatedStudents}
                        loading={loading}
                        onViewStudent={handleViewStudent}
                        onDeleteStudent={deleteStudent}
                    />
                ) : (
                    <StudentCardView />
                )}

                {/* Pagination */}
                <Flex mt="md" justify="space-between" align="center" wrap="wrap" gap="md">
                    <Text size="sm" c="dimmed">
                        Showing {rangeStart}-
                        {Math.min(currentPage * rowsPerPage, filteredStudents.length)} of {filteredStudents.length}
                    </Text>

                    <Group gap="md">
                        <Select
                            label="Rows per page"
                            value={rowsPerPage.toString()}
                            onChange={(value) => {
                                if (value) {
                                    setRowsPerPage(Number(value));
                                    setCurrentPage(1);
                                }
                            }}
                            data={["10", "15", "20", "30", "50"]}
                            style={{ width: "150px" }}
                        />

                        <Pagination
                            total={Math.ceil(filteredStudents.length / rowsPerPage)}
                            value={currentPage}
                            onChange={setCurrentPage}
                        />
                    </Group>
                </Flex>

                {/* Student Modal */}
                <StudentModal
                    opened={modalOpened}
                    onClose={handleCloseModal}
                    onSubmit={handleStudentSubmit}
                    student={selectedStudent}
                    checkEmailExists={checkEmailExists}
                    loading={loading}
                    mode={modalMode}
                />
            </Paper>
        </Container>
    );
};

export default StudentsPage;